import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './FilterBar.css'; // Importazione del file CSS specifico

const FilterBar = ({
  categories,
  characters,
  locations,
  selectedCategories,
  selectedCharacters,
  selectedLocations,
  onCategoryChange,
  onCharacterChange,
  onLocationChange,
  onClearFilters
}) => {
  const { t } = useTranslation();
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [characterDropdownOpen, setCharacterDropdownOpen] = useState(false);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const filterBarRef = useRef(null);

  // Normalizza le categorie per evitare duplicati dovuti a spazi/encoding
  const normalizedCategories = [...new Set(
    categories.map(category => category.trim())
  )].sort();

  // Gestisce i click fuori dal componente per chiudere i dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterBarRef.current && !filterBarRef.current.contains(event.target)) {
        setCategoryDropdownOpen(false);
        setCharacterDropdownOpen(false);
        setLocationDropdownOpen(false);
      }
    };

    // Aggiungi event listener quando il componente è montato
    document.addEventListener('mousedown', handleClickOutside);

    // Pulisci event listener quando il componente è smontato
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Funzione per pulire tutte le categorie selezionate
  const clearSelectedCategories = (e) => {
    e.stopPropagation();
    selectedCategories.forEach(category => onCategoryChange(category));
  };

  // Funzione per pulire tutti i personaggi selezionati
  const clearSelectedCharacters = (e) => {
    e.stopPropagation();
    selectedCharacters.forEach(id => onCharacterChange(id));
  };

  // Funzione per pulire tutte le locations selezionate
  const clearSelectedLocations = (e) => {
    e.stopPropagation();
    selectedLocations.forEach(location => onLocationChange(location));
  };

  return (
    <div className="filter-bar-container" ref={filterBarRef}>
      <div className="filter-bar-compact">
        <div className="filter-dropdowns">
          {/* Dropdown per categorie */}
          <div className="filter-dropdown">
            <button
              className="dropdown-toggle"
              onClick={() => {
                setCategoryDropdownOpen(!categoryDropdownOpen);
                if (characterDropdownOpen) setCharacterDropdownOpen(false);
                if (locationDropdownOpen) setLocationDropdownOpen(false);
              }}
            >
              {t('filterBar.categories')} {selectedCategories.length > 0 && `(${selectedCategories.length})`}
              <span className="dropdown-arrow">{categoryDropdownOpen ? '▲' : '▼'}</span>
            </button>

            {categoryDropdownOpen && (
              <div className="dropdown-menu">
                <div className="dropdown-header">
                  <h4>{t('filterBar.selectCategories')}</h4>
                  {selectedCategories.length > 0 && (
                    <button
                      className="clear-selection"
                      onClick={clearSelectedCategories}
                    >
                      {t('filterBar.clean')}
                    </button>
                  )}
                </div>
                <div className="dropdown-options">
                  {normalizedCategories.map(category => (
                    <label key={category} className="dropdown-option">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category)}
                        onChange={() => onCategoryChange(category)}
                      />
                      <span>{category}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Dropdown per personaggi */}
          <div className="filter-dropdown">
            <button
              className="dropdown-toggle"
              onClick={() => {
                setCharacterDropdownOpen(!characterDropdownOpen);
                if (categoryDropdownOpen) setCategoryDropdownOpen(false);
                if (locationDropdownOpen) setLocationDropdownOpen(false);
              }}
            >
              {t('filterBar.characters')} {selectedCharacters.length > 0 && `(${selectedCharacters.length})`}
              <span className="dropdown-arrow">{characterDropdownOpen ? '▲' : '▼'}</span>
            </button>

            {characterDropdownOpen && (
              <div className="dropdown-menu">
                <div className="dropdown-header">
                  <h4>{t('filterBar.selectCharacters')}</h4>
                  {selectedCharacters.length > 0 && (
                    <button
                      className="clear-selection"
                      onClick={clearSelectedCharacters}
                    >
                      {t('filterBar.clean')}
                    </button>
                  )}
                </div>
                <div className="dropdown-options characters-options">
                  {characters.map(character => (
                    <label key={character.id} className="dropdown-option">
                      <input
                        type="checkbox"
                        checked={selectedCharacters.includes(character.id)}
                        onChange={() => onCharacterChange(character.id)}
                      />
                      <span>{character.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Dropdown per locations */}
          {locations && locations.length > 0 && (
            <div className="filter-dropdown">
              <button
                className="dropdown-toggle"
                onClick={() => {
                  setLocationDropdownOpen(!locationDropdownOpen);
                  if (categoryDropdownOpen) setCategoryDropdownOpen(false);
                  if (characterDropdownOpen) setCharacterDropdownOpen(false);
                }}
              >
                {t('filterBar.locations')} {selectedLocations.length > 0 && `(${selectedLocations.length})`}
                <span className="dropdown-arrow">{locationDropdownOpen ? '▲' : '▼'}</span>
              </button>

              {locationDropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-header">
                    <h4>{t('filterBar.selectLocations')}</h4>
                    {selectedLocations.length > 0 && (
                      <button
                        className="clear-selection"
                        onClick={clearSelectedLocations}
                      >
                        {t('filterBar.clean')}
                      </button>
                    )}
                  </div>
                  <div className="dropdown-options">
                    {locations.map(location => (
                      <label key={location} className="dropdown-option">
                        <input
                          type="checkbox"
                          checked={selectedLocations.includes(location)}
                          onChange={() => onLocationChange(location)}
                        />
                        <span>{location}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {(selectedCategories.length > 0 || selectedCharacters.length > 0 || selectedLocations.length > 0) && (
          <button className="clear-all-button" onClick={onClearFilters}>
            {t('filterBar.removeAllFilters')}
          </button>
        )}
      </div>
    </div>
  );
};

export default FilterBar;
